import { FieldDef, GroupDef, GuiDef, ValidatorDef, TabDefinition, CollapsableDef } from '@/objects/gui';
import { MapTrack } from '@/objects/generated';
import { markRaw, computed } from 'vue';
import {
    InputText,
    ValidatedText,
    InputColor,
    DisplayColor,
    InputNullableFloat,
    DisplayDistance,
    InputSelect,
    DataSourceLookup,
} from '@/components';
import { useCommonStore } from '@/stores';
import { storeToRefs } from 'pinia';
import type { SelectLine, SelectLineValueType } from '@/objects/SelectLine';

export class MapTrackGuiDef extends GuiDef<MapTrack> {
    constructor() {
        super('MapTrack');

        const Common = storeToRefs(useCommonStore());
        const OrganizersSource = computed(() => {
            return Common.OrganizersAsSelectLine.value;
        });

        const Name = new FieldDef<MapTrack>();
        Name.Name = 'Name';
        Name.FieldName = 'Name';
        Name.DefinedFor = 'Both';
        Name.TranslationKey = 'maptrack.name';
        Name.Editable = true;
        Name.Filterable = true;
        Name.Sortable = true;
        Name.CssClasses = '';
        Name.value = (maptrack) => maptrack.Name;
        Name.setValue = (maptrack, value) => (maptrack.Name = value);
        Name.DetailEditor = markRaw(InputText);
        Name.DetailEditorProps = {};
        Name.CellEditor = markRaw(InputText);
        Name.CellEditorProps = {};
        Name.CellRenderer = markRaw(ValidatedText);
        Name.CellRendererProps = {};
        Name.Validator = null;
        Name.SortDirection = undefined;
        Name.cellClassRules = undefined;
        Name.MinWidth = undefined;
        Name.MaxWidth = undefined;
        Name.Tooltip = null;
        this.Fields.push(Name);

        const LineColor = new FieldDef<MapTrack>();
        LineColor.Name = 'LineColor';
        LineColor.FieldName = 'LineColor';
        LineColor.DefinedFor = 'Both';
        LineColor.TranslationKey = 'maptrack.linecolor';
        LineColor.Editable = true;
        LineColor.Filterable = true;
        LineColor.Sortable = true;
        LineColor.CssClasses = '';
        LineColor.value = (maptrack) => maptrack.LineColor;
        LineColor.setValue = (maptrack, value) => (maptrack.LineColor = value);
        LineColor.DetailEditor = markRaw(InputColor);
        LineColor.DetailEditorProps = {};
        LineColor.CellEditor = markRaw(InputColor);
        LineColor.CellEditorProps = {};
        LineColor.CellRenderer = markRaw(DisplayColor);
        LineColor.CellRendererProps = {};
        LineColor.Validator = null;
        LineColor.SortDirection = undefined;
        LineColor.cellClassRules = undefined;
        LineColor.MinWidth = undefined;
        LineColor.MaxWidth = undefined;
        LineColor.Tooltip = null;
        this.Fields.push(LineColor);

        const TrackLength = new FieldDef<MapTrack>();
        TrackLength.Name = 'TrackLength';
        TrackLength.FieldName = 'TrackLength';
        TrackLength.DefinedFor = 'List';
        TrackLength.TranslationKey = 'maptrack.tracklength';
        TrackLength.Editable = false;
        TrackLength.Filterable = true;
        TrackLength.Sortable = true;
        TrackLength.CssClasses = '';
        TrackLength.value = (maptrack) => maptrack.TrackLength;
        TrackLength.setValue = () => {};
        TrackLength.DetailEditor = markRaw(InputNullableFloat);
        TrackLength.DetailEditorProps = {};
        TrackLength.CellEditor = markRaw(InputNullableFloat);
        TrackLength.CellEditorProps = {};
        TrackLength.CellRenderer = markRaw(DisplayDistance);
        TrackLength.CellRendererProps = {};
        TrackLength.Validator = null;
        TrackLength.SortDirection = undefined;
        TrackLength.cellClassRules = undefined;
        TrackLength.MinWidth = undefined;
        TrackLength.MaxWidth = undefined;
        TrackLength.Tooltip = null;
        this.Fields.push(TrackLength);

        const Description = new FieldDef<MapTrack>();
        Description.Name = 'Description';
        Description.FieldName = 'Description';
        Description.DefinedFor = 'Both';
        Description.TranslationKey = 'maptrack.description';
        Description.Editable = true;
        Description.Filterable = true;
        Description.Sortable = true;
        Description.CssClasses = '';
        Description.value = (maptrack) => maptrack.Description;
        Description.setValue = (maptrack, value) => (maptrack.Description = value);
        Description.DetailEditor = markRaw(InputText);
        Description.DetailEditorProps = {};
        Description.CellEditor = markRaw(InputText);
        Description.CellEditorProps = {};
        Description.CellRenderer = markRaw(ValidatedText);
        Description.CellRendererProps = {};
        Description.Validator = null;
        Description.SortDirection = undefined;
        Description.cellClassRules = undefined;
        Description.MinWidth = undefined;
        Description.MaxWidth = undefined;
        Description.Tooltip = null;
        this.Fields.push(Description);

        const OrganizerID = new FieldDef<MapTrack>();
        OrganizerID.Name = 'OrganizerID';
        OrganizerID.FieldName = 'OrganizerID';
        OrganizerID.DefinedFor = 'Both';
        OrganizerID.TranslationKey = 'maptrack.organizerid';
        OrganizerID.Editable = true;
        OrganizerID.Filterable = false;
        OrganizerID.Sortable = true;
        OrganizerID.CssClasses = '';
        OrganizerID.value = (maptrack) => maptrack.OrganizerID;
        OrganizerID.setValue = (maptrack, value) => {
            if (value === null || value === undefined || value === '' || isNaN(value)) {
                value = 0;
            }
            maptrack.OrganizerID = value;
        };
        OrganizerID.DetailEditor = markRaw(InputSelect);
        OrganizerID.DetailEditorProps = { datasource: { mapsource: OrganizersSource } };
        OrganizerID.CellEditor = markRaw(InputSelect);
        OrganizerID.CellEditorProps = { datasource: { mapsource: OrganizersSource } };
        // @ts-expect-error @ts-ignore
        OrganizerID.CellRenderer = markRaw(DataSourceLookup);
        OrganizerID.CellRendererProps = { datasource: { mapsource: OrganizersSource } };
        OrganizerID.ExportValueFormatter = (obj: MapTrack) => {
            return OrganizersSource.value.get(obj.OrganizerID)?.text;
        };
        OrganizerID.Validator = null;
        OrganizerID.SortDirection = undefined;
        OrganizerID.cellClassRules = undefined;
        OrganizerID.MinWidth = undefined;
        OrganizerID.MaxWidth = undefined;
        OrganizerID.Tooltip = null;
        this.Fields.push(OrganizerID);
    }

    public async LoadDependencies() {
        await useCommonStore().EnsureLoaded(['Organizers']);
    }
}
